'use client';

import { useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { BookCheck, Clock3, RefreshCw } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  AnimatedNumber,
  Avatar,
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  FakeQr,
  FloatingCard,
  Reveal,
  SPRING_SNAPPY,
  localize,
  useDemoTimeline,
} from './demo-kit';
import { PAYMENTS_DEMO_SIZE } from './demo-sizes';

/*
 * Payments: four players pay their share of one court booking, the booking
 * settles, a tax-compliant e-invoice is issued and synced to accounting, and
 * the ledger row reconciles. Statuses only, no amounts, so no currency.
 */


const DURATIONS = [1200, 850, 850, 850, 1300, 2000, 3000] as const;

const STEP = {
  fullyPaid: 4,
  invoice: 5,
  ledger: 6,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of payments: four players each pay their share of a court booking, the booking is fully paid, a tax e-invoice is issued and synced to accounting, and the payment is reconciled.',
    ar: 'عرض متحرك للمدفوعات: أربعة لاعبين يدفع كل منهم حصته من حجز الملعب، فيكتمل الدفع، وتصدر فاتورة ضريبية إلكترونية تُزامن مع المحاسبة، ثم تتم مطابقة الدفعة.',
  },
  booking: { en: 'Booking #2481', ar: 'حجز رقم 2481' },
  awaiting: { en: 'Awaiting players', ar: 'بانتظار اللاعبين' },
  fullyPaid: { en: 'Fully paid', ar: 'مدفوع بالكامل' },
  title: { en: 'Court 3 · Thu 8:00 PM', ar: 'الملعب 3 · الخميس 8:00 م' },
  subtitle: { en: 'Split payment · 4 players', ar: 'دفع مقسّم · 4 لاعبين' },
  paidOf: { en: 'of 4 paid', ar: 'من 4 دفعوا' },
  pending: { en: 'Pending', ar: 'معلّق' },
  paid: { en: 'Paid', ar: 'مدفوع' },
  invoice: { en: 'Tax invoice', ar: 'فاتورة ضريبية' },
  line: { en: 'Padel court · 90 min', ar: 'ملعب بادل · 90 دقيقة' },
  players: { en: 'Players', ar: 'اللاعبون' },
  tax: { en: 'Tax', ar: 'الضريبة' },
  total: { en: 'Total', ar: 'الإجمالي' },
  issued: { en: 'E-invoice issued', ar: 'صدرت الفاتورة الإلكترونية' },
  synced: { en: 'Synced to your accounting', ar: 'تمت المزامنة مع محاسبتك' },
  wafeq: { en: 'Wafeq', ar: 'وافق' },
  ledger: { en: 'Payments ledger', ar: 'سجل المدفوعات' },
  reconciled: { en: 'Reconciled', ar: 'تمت المطابقة' },
  automatic: { en: 'Matched automatically', ar: 'طوبقت تلقائيًا' },
  justNow: { en: 'Just now', ar: 'الآن' },
} as const;

type Method = 'applePay' | 'mada' | 'visa';

const PLAYERS: {
  name: { en: string; ar: string };
  initials: { en: string; ar: string };
  paidLine: { en: string; ar: string };
  tone: number;
  method: Method;
}[] = [
  {
    name: { en: 'Sara', ar: 'سارة' },
    initials: { en: 'SA', ar: 'س' },
    paidLine: { en: 'Sara paid her share', ar: 'سارة دفعت حصتها' },
    tone: 1,
    method: 'applePay',
  },
  {
    name: { en: 'Majed', ar: 'ماجد' },
    initials: { en: 'MJ', ar: 'م' },
    paidLine: { en: 'Majed paid his share', ar: 'ماجد دفع حصته' },
    tone: 3,
    method: 'mada',
  },
  {
    name: { en: 'Rayan', ar: 'ريان' },
    initials: { en: 'RY', ar: 'ر' },
    paidLine: { en: 'Rayan paid his share', ar: 'ريان دفع حصته' },
    tone: 4,
    method: 'visa',
  },
  {
    name: { en: 'Lina', ar: 'لينا' },
    initials: { en: 'LN', ar: 'ل' },
    paidLine: { en: 'Lina paid her share', ar: 'لينا دفعت حصتها' },
    tone: 0,
    method: 'applePay',
  },
];

export function PaymentsDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS);
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.padel;
  const step = timeline.step;
  const paidCount = Math.min(step, PLAYERS.length);
  const fullyPaid = step >= STEP.fullyPaid;
  const fromEnd = locale === 'ar' ? -44 : 44;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <div className="relative" style={PAYMENTS_DEMO_SIZE}>
        {/* Booking with split payment */}
        <FloatingCard className="absolute start-0 top-[8px] w-[330px] p-4">
          <div className="flex items-center justify-between">
            <span className="font-roboto-mono text-[9.5px] uppercase tracking-[0.14em] text-[#8a857c]">
              {copy.booking}
            </span>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={fullyPaid ? 'paid' : 'awaiting'}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                  fullyPaid
                    ? 'bg-[#ddf5ec] text-[#2f7a63]'
                    : 'bg-[#fbf0dc] text-[#9a6412]',
                )}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.25 }}
              >
                {fullyPaid ? copy.fullyPaid : copy.awaiting}
              </motion.span>
            </AnimatePresence>
          </div>

          <div className="mt-2.5 flex items-center gap-2.5">
            <BrandMark brand={brand} size={34} />
            <div>
              <p className="text-[15px] font-bold leading-tight text-[#202020]">{copy.title}</p>
              <p className="mt-0.5 text-[11px] text-[#8a857c]">{copy.subtitle}</p>
            </div>
          </div>

          <div className="mt-4 flex items-baseline justify-between text-[11px]">
            <span className="text-[#202020]">
              <span className="font-semibold tabular-nums">{paidCount}</span> {copy.paidOf}
            </span>
            <span className="font-roboto-mono text-[10.5px] text-[#8a857c]">
              <AnimatedNumber value={paidCount * 25} duration={0.6} />%
            </span>
          </div>
          <div className="mt-1.5 h-[6px] overflow-hidden rounded-full bg-[#ede8e2]">
            <motion.div
              className="h-full rounded-full"
              initial={false}
              animate={{
                width: `${paidCount * 25}%`,
                backgroundColor: fullyPaid ? DEMO_COLORS.success : brand.accent,
              }}
              transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
            />
          </div>

          <div className="mt-3 divide-y divide-[#f0ede8]">
            {PLAYERS.map((player, index) => {
              const paid = index < paidCount;

              return (
                <div key={player.name.en} className="flex h-[44px] items-center gap-2.5">
                  <Avatar initials={player.initials[locale]} tone={player.tone} size={28} />
                  <span className="flex-1 text-[12.5px] font-medium text-[#202020]">
                    {player.name[locale]}
                  </span>
                  <MethodPill method={player.method} />
                  <StatusChip paid={paid} paidLabel={copy.paid} pendingLabel={copy.pending} />
                </div>
              );
            })}
          </div>
        </FloatingCard>

        {/* Live payment toast */}
        <div className="absolute end-0 top-[8px] w-[216px]">
          <AnimatePresence mode="popLayout" initial={false}>
            {paidCount > 0 && step < STEP.invoice && (
              <motion.div
                key={paidCount}
                initial={{ opacity: 0, y: -14, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.96 }}
                transition={{ duration: 0.4, ease: EASE_OUT_EXPO }}
              >
                <FloatingCard className="flex items-center gap-2.5 px-3 py-2.5">
                  <Avatar
                    initials={PLAYERS[paidCount - 1]!.initials[locale]}
                    tone={PLAYERS[paidCount - 1]!.tone}
                    size={28}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-semibold text-[#202020]">
                      {PLAYERS[paidCount - 1]!.paidLine[locale]}
                    </p>
                    <p className="text-[9.5px] text-[#8a857c]">{copy.justNow}</p>
                  </div>
                  <CheckBadge show size={18} delay={0.15} />
                </FloatingCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tax e-invoice */}
        <motion.div
          className="absolute end-0 top-[150px] z-10 w-[240px]"
          initial={false}
          animate={{
            opacity: step >= STEP.invoice ? 1 : 0,
            x: step >= STEP.invoice ? 0 : fromEnd,
            rotate: step >= STEP.invoice ? 0 : locale === 'ar' ? -3 : 3,
          }}
          transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
        >
          <FloatingCard className="p-4 shadow-[0_30px_60px_-24px_rgba(32,32,32,0.45)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[13.5px] font-bold text-[#202020]">{copy.invoice}</p>
                <p className="font-roboto-mono text-[9.5px] text-[#8a857c]" dir="ltr">
                  INV-2026-04817
                </p>
              </div>
              <BrandMark brand={brand} size={24} />
            </div>

            <div className="mt-3 space-y-2 border-y border-dashed border-[#dcd7cf] py-3">
              <InvoiceLine label={copy.line} barWidth={42} />
              <InvoiceLine label={copy.players} barWidth={18} />
              <InvoiceLine label={copy.tax} barWidth={30} />
              <InvoiceLine label={copy.total} barWidth={48} strong />
            </div>

            <div className="mt-3 flex items-center gap-3">
              <div className="rounded-[8px] border border-[#ebe7e1] p-1">
                <FakeQr size={58} seed={21} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Reveal show={step >= STEP.invoice} delay={0.45}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#ddf5ec] py-0.5 pe-2 ps-0.5 text-[10px] font-medium text-[#2f7a63]">
                    <CheckBadge show={step >= STEP.invoice} size={14} delay={0.55} />
                    {copy.issued}
                  </span>
                </Reveal>
                <Reveal show={step >= STEP.invoice} delay={0.8}>
                  <p className="flex items-center gap-1 text-[9.5px] leading-tight text-[#8a857c]">
                    <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.4} />
                    {copy.synced}
                  </p>
                  <p className="mt-0.5 ps-3.5 text-[10px] font-semibold text-[#202020]">
                    {copy.wafeq}
                  </p>
                </Reveal>
              </div>
            </div>
          </FloatingCard>
        </motion.div>

        {/* Ledger reconciliation */}
        <motion.div
          className="absolute bottom-[12px] start-0 w-[330px]"
          initial={false}
          animate={{
            opacity: step >= STEP.ledger ? 1 : 0,
            y: step >= STEP.ledger ? 0 : 16,
          }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
        >
          <FloatingCard className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#e6f0ff] text-[#2c4c80]">
              <BookCheck className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-[#202020]">{copy.ledger}</p>
              <p className="truncate text-[10.5px] text-[#8a857c]">{copy.automatic}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#2f7a63]">
              <CheckBadge show={step >= STEP.ledger} size={18} delay={0.3} />
              {copy.reconciled}
            </span>
          </FloatingCard>
        </motion.div>
      </div>
    </DemoStage>
  );
}

function StatusChip({
  paid,
  paidLabel,
  pendingLabel,
}: {
  paid: boolean;
  paidLabel: string;
  pendingLabel: string;
}) {
  return (
    <span className="relative flex w-[74px] justify-end">
      <AnimatePresence mode="wait" initial={false}>
        {paid ? (
          <motion.span
            key="paid"
            className="inline-flex items-center gap-1 rounded-full bg-[#ddf5ec] py-0.5 pe-2 ps-0.5 text-[10px] font-medium text-[#2f7a63]"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={SPRING_SNAPPY}
          >
            <CheckBadge show size={14} delay={0.05} />
            {paidLabel}
          </motion.span>
        ) : (
          <motion.span
            key="pending"
            className="inline-flex items-center gap-1 rounded-full bg-[#fbf0dc] px-2 py-0.5 text-[10px] font-medium text-[#9a6412]"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.18 }}
          >
            <Clock3 className="h-3 w-3" strokeWidth={2.2} />
            {pendingLabel}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

function MethodPill({ method }: { method: Method }) {
  if (method === 'applePay') {
    return (
      <span className="rounded-[5px] bg-black px-1.5 py-[3px] text-[8.5px] font-semibold text-white" dir="ltr">
        Apple Pay
      </span>
    );
  }

  if (method === 'mada') {
    return (
      <span className="rounded-[5px] bg-gradient-to-br from-[#2d6a4f] to-[#1b4332] px-1.5 py-[3px] font-roboto-mono text-[8.5px] font-bold text-white">
        mada
      </span>
    );
  }

  return (
    <span className="rounded-[5px] border border-[#dcd7cf] bg-white px-1.5 py-[2px] text-[9px] font-black italic tracking-tight text-[#1a1f71]">
      VISA
    </span>
  );
}

function InvoiceLine({
  label,
  barWidth,
  strong = false,
}: {
  label: string;
  barWidth: number;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={cn('text-[10.5px]', strong ? 'font-semibold text-[#202020]' : 'text-[#5c554b]')}>
        {label}
      </span>
      <span
        className={cn('h-[7px] rounded-full', strong ? 'bg-[#cfc9bf]' : 'bg-[#ede8e2]')}
        style={{ width: barWidth }}
      />
    </div>
  );
}
